import { Fragment, useMemo } from "react";
import { CodeBlock } from "./CodeBlock";

interface MarkdownProps {
  content: string;
}

// ── Block types ──

type Block =
  | { type: "code"; language?: string; code: string }
  | { type: "heading"; level: number; inline: InlineNode[] }
  | { type: "hr" }
  | { type: "ul"; items: InlineNode[][] }
  | { type: "ol"; items: InlineNode[][]; start: number }
  | { type: "paragraph"; inline: InlineNode[] };

// ── Inline types ──

type InlineNode =
  | { type: "text"; text: string }
  | { type: "code"; text: string }
  | { type: "bold"; children: InlineNode[] }
  | { type: "italic"; children: InlineNode[] }
  | { type: "link"; href: string; children: InlineNode[] };

// ── Block parser ──

function parseBlocks(src: string): Block[] {
  const lines = src.split("\n");
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i]!;

    // Fenced code block
    const fenceMatch = line.match(/^```(\w*)/);
    if (fenceMatch) {
      const language = fenceMatch[1] || undefined;
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i]!.startsWith("```")) {
        codeLines.push(lines[i]!);
        i++;
      }
      i++; // skip closing ```
      blocks.push({ type: "code", language, code: codeLines.join("\n") });
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,4})\s+(.+)/);
    if (headingMatch) {
      blocks.push({
        type: "heading",
        level: headingMatch[1]!.length,
        inline: parseInline(headingMatch[2]!),
      });
      i++;
      continue;
    }

    // Horizontal rule
    if (/^(-{3,}|\*{3,}|_{3,})\s*$/.test(line)) {
      blocks.push({ type: "hr" });
      i++;
      continue;
    }

    // Unordered list
    if (/^[\-\*]\s+/.test(line)) {
      const items: InlineNode[][] = [];
      while (i < lines.length && /^[\-\*]\s+/.test(lines[i]!)) {
        items.push(parseInline(lines[i]!.replace(/^[\-\*]\s+/, "")));
        i++;
      }
      blocks.push({ type: "ul", items });
      continue;
    }

    // Ordered list
    const olMatch = line.match(/^(\d+)\.\s+/);
    if (olMatch) {
      const start = parseInt(olMatch[1]!, 10);
      const items: InlineNode[][] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i]!)) {
        items.push(parseInline(lines[i]!.replace(/^\d+\.\s+/, "")));
        i++;
      }
      blocks.push({ type: "ol", items, start });
      continue;
    }

    // Blank line — skip
    if (line.trim() === "") {
      i++;
      continue;
    }

    // Paragraph — collect contiguous non-blank, non-special lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i]!.trim() !== "" &&
      !lines[i]!.match(/^```/) &&
      !lines[i]!.match(/^#{1,4}\s+/) &&
      !lines[i]!.match(/^(-{3,}|\*{3,}|_{3,})\s*$/) &&
      !lines[i]!.match(/^[\-\*]\s+/) &&
      !lines[i]!.match(/^\d+\.\s+/)
    ) {
      paraLines.push(lines[i]!);
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({
        type: "paragraph",
        inline: parseInline(paraLines.join("\n")),
      });
    }
  }

  return blocks;
}

// ── Inline parser ──

function parseInline(src: string): InlineNode[] {
  const nodes: InlineNode[] = [];
  let remaining = src;

  while (remaining.length > 0) {
    // Inline code (backtick)
    const codeMatch = remaining.match(/^`([^`]+)`/);
    if (codeMatch) {
      nodes.push({ type: "code", text: codeMatch[1]! });
      remaining = remaining.slice(codeMatch[0]!.length);
      continue;
    }

    // Bold (**text**)
    const boldMatch = remaining.match(/^\*\*(.+?)\*\*/s);
    if (boldMatch) {
      nodes.push({ type: "bold", children: parseInline(boldMatch[1]!) });
      remaining = remaining.slice(boldMatch[0]!.length);
      continue;
    }

    // Italic (*text*) — must not be **
    const italicMatch = remaining.match(/^\*([^*]+?)\*/);
    if (italicMatch) {
      nodes.push({ type: "italic", children: parseInline(italicMatch[1]!) });
      remaining = remaining.slice(italicMatch[0]!.length);
      continue;
    }

    // Link [text](url)
    const linkMatch = remaining.match(/^\[([^\]]+)\]\(([^)]+)\)/);
    if (linkMatch) {
      nodes.push({
        type: "link",
        href: linkMatch[2]!,
        children: parseInline(linkMatch[1]!),
      });
      remaining = remaining.slice(linkMatch[0]!.length);
      continue;
    }

    // Plain text — consume until next special character or end
    const textMatch = remaining.match(/^[^`*\[]+/);
    if (textMatch) {
      nodes.push({ type: "text", text: textMatch[0]! });
      remaining = remaining.slice(textMatch[0]!.length);
      continue;
    }

    // Fallback: consume one character as text (unmatched special char)
    nodes.push({ type: "text", text: remaining[0]! });
    remaining = remaining.slice(1);
  }

  return nodes;
}

// ── Inline renderer ──

function renderInline(nodes: InlineNode[]): React.ReactNode[] {
  return nodes.map((node, i) => {
    switch (node.type) {
      case "text":
        // Preserve newlines within paragraphs
        return node.text.includes("\n") ? (
          <Fragment key={i}>
            {node.text.split("\n").map((line, j, arr) => (
              <Fragment key={j}>
                {line}
                {j < arr.length - 1 && <br />}
              </Fragment>
            ))}
          </Fragment>
        ) : (
          <Fragment key={i}>{node.text}</Fragment>
        );
      case "code":
        return (
          <code
            key={i}
            className="rounded-md bg-white/[0.05] px-1.5 py-0.5 text-[13px] font-mono text-intri-accent/80 break-all"
          >
            {node.text}
          </code>
        );
      case "bold":
        return (
          <strong key={i} className="font-semibold text-intri-text">
            {renderInline(node.children)}
          </strong>
        );
      case "italic":
        return (
          <em key={i} className="italic">
            {renderInline(node.children)}
          </em>
        );
      case "link":
        return (
          <a
            key={i}
            href={node.href}
            target="_blank"
            rel="noopener noreferrer"
            className="text-intri-accent/70 underline underline-offset-2 decoration-intri-accent/20 hover:text-intri-accent/90 hover:decoration-intri-accent/40 transition-colors"
          >
            {renderInline(node.children)}
          </a>
        );
    }
  });
}

// ── Block renderer ──

function renderBlock(block: Block, index: number): React.ReactNode {
  switch (block.type) {
    case "code":
      return <CodeBlock key={index} code={block.code} language={block.language} />;
    case "heading": {
      const Tag = `h${block.level}` as "h1" | "h2" | "h3" | "h4";
      const sizeClass =
        block.level === 1
          ? "text-base font-semibold"
          : block.level === 2
            ? "text-[15px] font-semibold"
            : block.level === 3
              ? "text-sm font-semibold"
              : "text-sm font-medium";
      return (
        <Tag key={index} className={`${sizeClass} text-intri-text mt-4 mb-1.5`}>
          {renderInline(block.inline)}
        </Tag>
      );
    }
    case "hr":
      return (
        <hr key={index} className="my-5 border-0 h-px bg-white/[0.04]" />
      );
    case "ul":
      return (
        <ul key={index} className="my-2 ml-4 space-y-1">
          {block.items.map((item, i) => (
            <li key={i} className="relative pl-3.5 text-sm leading-relaxed before:absolute before:left-0 before:top-[0.6em] before:h-1 before:w-1 before:rounded-full before:bg-intri-muted/40">
              {renderInline(item)}
            </li>
          ))}
        </ul>
      );
    case "ol":
      return (
        <ol
          key={index}
          start={block.start}
          className="my-2 ml-4 space-y-1 list-decimal list-outside marker:text-intri-dim/60 marker:text-xs"
        >
          {block.items.map((item, i) => (
            <li key={i} className="pl-1.5 text-sm leading-relaxed">
              {renderInline(item)}
            </li>
          ))}
        </ol>
      );
    case "paragraph":
      return (
        <p key={index} className="text-sm leading-relaxed mb-2 last:mb-0">
          {renderInline(block.inline)}
        </p>
      );
  }
}

// ── Main component ──

export function Markdown({ content }: MarkdownProps) {
  const blocks = useMemo(() => parseBlocks(content), [content]);

  return (
    <div className="markdown-body overflow-hidden break-words">
      {blocks.map((block, i) => renderBlock(block, i))}
    </div>
  );
}
